import EpiApiClient from "@/services/serviceClients/epiApiClient";
import { 
    ESTIMATE_API_PATH,
    ESTIMATOR_SETTINGS_API_PATH,
    ESTIMATOR_ROOF_PITCH_API_PATH
} from "@/config/constants";

class createEstimatorService {
    // Get roof pitch values
    static async getRoofPitchValues() {
        try {
            const response = await EpiApiClient.get(ESTIMATOR_ROOF_PITCH_API_PATH);
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description || 
                                error.message || 
                                'Failed to load roof pitch values';
            console.error('createEstimatorService.getRoofPitchValues failed:', errorMessage, error);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    // Save estimate using legacy endpoint (matches EstimatorLandingPage.cshtml)
    static async getEstimateById(estimateId) {
        try {
            if ( estimateId === undefined || estimateId === null) {
                throw new Error('Invalid estimate ID');
            }
            const url = `${ESTIMATE_API_PATH}/${estimateId}`;
            const response = await EpiApiClient.get(url);
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description ||  error.message || 'Unable To Save Estimate. Please Try Again.';
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    static async saveEstimate(payload) {
        try {
            const response = await EpiApiClient.post(ESTIMATE_API_PATH, payload);
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description ||  error.message || 'Unable To Save Estimate. Please Try Again.';
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    // Get estimator settings (matches legacy getEstimatorEstimateSettings)
    static async getEstimatorSettings() {
        try {
            const response = await EpiApiClient.get(ESTIMATOR_SETTINGS_API_PATH);
            return {
                success: true,
                data: response.data,
                error: null
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error_description || 
                                error.message || 
                                'Failed to load estimator settings';
            console.error('createEstimatorService.getEstimatorSettings failed:', errorMessage, error);
            return {
                success: false,
                data: null,
                error: errorMessage
            };
        }
    }

    // ===== VALIDATION METHODS =====
    
    static validateZipCode(zipCode) {
        if (!zipCode || typeof zipCode !== 'string') {
            return false;
        }
        const zipPattern = /^\d{5}(-\d{4})?$/;
        return zipPattern.test(zipCode.trim());
    }

    static validateAddress(address) {
        if (!address || typeof address !== 'object') {
            return false;
        }

        const requiredFields = ['shipToAddress1', 'shipToAddressCity', 'shipToAddressState', 'shipToAddressZipCode'];
        const hasRequiredFields = requiredFields.every(field => 
            address[field] && typeof address[field] === 'string' && address[field].trim().length > 0
        );

        if (!hasRequiredFields) {
            return false;
        }

        return this.validateZipCode(address.shipToAddressZipCode);
    }

    static validateJobInformation(jobInfo) {
        if (!jobInfo || typeof jobInfo !== 'object') {
            return false;
        }

        if (!jobInfo.jobName || typeof jobInfo.jobName !== 'string' || jobInfo.jobName.trim().length === 0) {
            return false;
        }

        if (!jobInfo.branchCode || typeof jobInfo.branchCode !== 'string') {
            return false;
        }

        if (!jobInfo.jobAccountId) {
            return false;
        }

        return true;
    }

    // ===== UTILITY METHODS =====
    
    static parseGoogleAddressComponents(addressComponents) {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return null;
        }

        const parsed = {
            shipToAddress1: '',
            shipToAddressCity: '',
            shipToAddressState: '',
            shipToAddressZipCode: '',
            county: ''
        };

        addressComponents.forEach(component => {
            const types = component.types || [];
            const value = component.long_name || '';

            if (types.includes('street_number')) {
                parsed.shipToAddress1 = value;
            } else if (types.includes('route')) {
                parsed.shipToAddress1 = parsed.shipToAddress1 ? `${parsed.shipToAddress1} ${value}` : value;
            } else if (types.includes('locality')) {
                parsed.shipToAddressCity = value;
            } else if (types.includes('administrative_area_level_1')) {
                parsed.shipToAddressState = component.short_name || value;
            } else if (types.includes('postal_code')) {
                parsed.shipToAddressZipCode = value;
            } else if (types.includes('administrative_area_level_2')) {
                parsed.county = value.replace(' County', '');
            }
        });

        return parsed;
    }

    static formatFullAddress(address) {
        if (!address || typeof address !== 'object') {
            return '';
        }

        const parts = [];
        
        if (address.shipToAddress1) parts.push(address.shipToAddress1);
        if (address.shipToAddress2) parts.push(address.shipToAddress2);
        if (address.shipToAddressCity) parts.push(address.shipToAddressCity);
        if (address.shipToAddressState) parts.push(address.shipToAddressState);
        if (address.shipToAddressZipCode) parts.push(address.shipToAddressZipCode);

        return parts.join(', ');
    }
}

export default createEstimatorService;

